import BaseConfiguratorService from '@configuratorware/configurator-frontendgui/App/Services/ConfiguratorService';
import { SaveTypes } from '@configuratorware/configurator-frontendgui/App/configuration';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import { switchOptions } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { ActionTypes } from '../Reducers/App/types';
import { setDoor } from '../Reducers/Configurator/Actions';
import { setClickToAddDoorHintState, setContextMenuState } from '../Reducers/App/Actions';
import { getPointFromPointerEvent } from '../Utils/pointerEvents';
import { omit } from 'lodash';
import { getDoorType } from '../Reducers/Configurator/Selectors';

const getFirstNestedValue = (object) => {
  const value = Object.values(object)[0];
  return typeof value === 'object' && value ? getFirstNestedValue(value) : value;
};

class ConfiguratorService extends BaseConfiguratorService {
  async addConfigurationToList(options = {}) {
    const { temporary = false } = options;
    await this.captureAndSaveConfiguration(SaveTypes.USER);
    const { configuration, calculation } = Services.store.state.configurator;
    const previewImage = this.lastScreenshot;
    Services.store.dispatch({
      type: ActionTypes.ADD_CONFIGURATION_TO_LIST,
      configuration: {
        ...configuration,
        previewImage,
        calculation,
        temporary,
      },
    });
  }

  async createMediaDataForConfiguration(configuration) {
    this.lastScreenshot = null;
    const mediaData = await super.createMediaDataForConfiguration(configuration);
    this.lastScreenshot = getFirstNestedValue(mediaData.screenshots);
  }

  getRoughConfiguration(configuration) {
    const { item, optionclassifications } = configuration;
    const customdata = configuration.customdata && omit(configuration.customdata, ['comment']);

    return {
      item: item && {
        identifier: item.identifier,
        price: item.price,
      },
      optionclassifications: optionclassifications?.map((oc) => ({
        identifier: oc.identifier,
        selectedoptions: oc.selectedoptions?.map((o) => ({
          identifier: o.identifier,
          amount: o.amount,
          price: o.price,
          inputText: o.inputText,
        })),
      })),
      customdata,
    };
  }

  selectFirstDoorTypeIfNotSet() {
    const configuration = this.state.configuration;
    const currentDoorType = getDoorType(configuration)?.identifier;
    if (currentDoorType && currentDoorType !== 'no_door') {
      return;
    }
    const switchOptionData = {
      door_types: {
        identifier: 'door_glass',
        amount: 1,
      },
    };
    return Services.store.dispatch(switchOptions(configuration, switchOptionData, false, false, true));
  }

  syncDoorTypeAmount() {
    const configuration = this.state.configuration;
    const selectedDoorType = getDoorType(configuration);
    const selectedAmount = selectedDoorType?.amount;
    const doorCount = configuration.customdata.doors.length;
    if (selectedAmount !== doorCount) {
      const switchOptionData = {
        door_types: {
          identifier: selectedDoorType?.identifier || 'glass_door',
          amount: doorCount,
        },
      };
      return Services.store.dispatch(switchOptions(configuration, switchOptionData, false, false, true));
    }
  }

  async handleWallSegmentClick(evt, wall, position) {
    const wallDoors = this.state.configuration.customdata.doors?.filter((door) => door.wall === wall);
    const existingDoorAtPosition = wallDoors?.some((door) => door.position === position);
    if (existingDoorAtPosition) {
      const { x: left = 0, y: top = 0 } = getPointFromPointerEvent(evt);
      Services.store.dispatch(setContextMenuState({ open: true, top, left, target: { wall, position } }));
    } else if (wallDoors?.length >= 2) {
      Services.ui.reportError(t('hintMessages.maximumDoorAmountReached'));
    } else {
      Services.store.dispatch(setClickToAddDoorHintState({ show: false, top: 0, left: 0 }));
      await this.setDoorAtPosition(wall, position);
    }
    this.syncDoorTypeAmount();
  }

  async setDoorAtPosition(wall, index) {
    await this.selectFirstDoorTypeIfNotSet();
    Services.store.dispatch(setDoor(wall, index));
  }
}

export default ConfiguratorService;
